import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react";

type Direction = "up" | "left" | "right" | "scale";

interface ScrollRevealProps {
  children: ReactNode;
  /** Stagger within a group, in milliseconds. */
  delay?: number;
  direction?: Direction;
  as?: ElementType;
  className?: string;
}

const DIRECTION_CLASS: Record<Direction, string> = {
  up: "",
  left: "gw-reveal--left",
  right: "gw-reveal--right",
  scale: "gw-reveal--scale",
};

/**
 * Reveals content as it scrolls into view.
 *
 * Two safeguards keep this from ever hiding content permanently:
 *
 *  1. The hiding class is applied from an effect, not during render. If
 *     JavaScript fails to run, or the prerendered HTML is read by a crawler
 *     or a text browser, the content is simply visible.
 *  2. Users who prefer reduced motion never get the class at all, so there
 *     is no transition and nothing to wait for.
 *
 * The observer disconnects after the first reveal: this is an entrance
 * effect, not something that should re-hide content when scrolling back up.
 */
export const ScrollReveal = ({
  children,
  delay = 0,
  direction = "up",
  as: Component = "div",
  className = "",
}: ScrollRevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [animated, setAnimated] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    // Already on screen at mount (above the fold): reveal without waiting,
    // otherwise the hero would animate in after the page is visibly ready.
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) {
      setAnimated(true);
      const frame = window.requestAnimationFrame(() => setRevealed(true));
      return () => window.cancelAnimationFrame(frame);
    }

    setAnimated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        }
      },
      // Trigger slightly before the element is fully visible so the motion
      // finishes about when it reaches a comfortable reading position.
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const classes = [
    animated ? "gw-reveal" : "",
    animated ? DIRECTION_CLASS[direction] : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component
      ref={ref}
      className={classes || undefined}
      data-revealed={animated ? String(revealed) : undefined}
      style={
        delay > 0
          ? ({ "--gw-reveal-delay": `${delay}ms` } as React.CSSProperties)
          : undefined
      }
    >
      {children}
    </Component>
  );
};
